<template>
  <div class="client-introduction-wrapper">
    <!-- Loading Component for both save operations and assignment data loading -->
    <IsLoadingComponent v-if="showLoading || isLoadingAssignmentData || isLoadingPropertyContacts" />

    <!-- Form Content (disabled while loading) -->
    <div :class="{ 'form-disabled': showLoading || isLoadingAssignmentData || isLoadingPropertyContacts }">
      <!-- Selected Assignment and Email Section -->
      <SelectedEmailAndAssignment 
        :emailData="emailData"
        :assignment="selectedAssignment"
        :disableAssignmentClick="true"
        class="mb-4"
        @assignmentChanged="handleAssignmentChanged"
      />

      <!-- Reply to Selected Email Checkbox -->
      <div v-if="hasEmailForReply" class="form-field reply-checkbox-section">
        <div class="reply-checkbox-container">
          <v-checkbox
            v-model="formData.replyToSelectedEmail"
            color="#003f2d"
            hide-details
            class="reply-checkbox"
          />
          <label class="field-label checkbox-main-label">Reply to Selected Email</label>
        </div>
        <div class="checkbox-description">
          Replying to the selected email will keep the email's subject and recipient list
        </div>
      </div>

      <!-- Client Contact Statement Selection -->
      <IntroParagraphSelector
        v-model="formData.selectedIntroParagraph"
        label="Client Contact Statement*"
        dialogTitle="Custom Client Contact Statement"
        dialogDescription="Create a custom Client Contact statement to send to the client."
        nameLabel="Statement Name*"
        contentLabel="Statement*"
        storageContext="clientIntroduction"
        @paragraph-changed="onIntroParagraphChanged"
        @error="handleIntroParagraphError"
        @success="handleIntroParagraphSuccess"
        class="form-field"
      />

      <!-- Client Contact Fields -->
      <div class="form-field">
        <label class="field-label">Client Contact*</label>
        <div v-if="isLoadingAssignmentData" class="loading-indicator">
          <v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
          <span class="loading-text">Loading contact details...</span>
        </div>
        <div v-else>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formData.clientContactFirstName"
                @blur="normalizeClientContactName('clientContactFirstName')"
                placeholder="First Name"
                variant="outlined"
                density="compact"
                hide-details
                autocomplete="new-password"
                spellcheck="false"
                :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formData.clientContactLastName"
                @blur="normalizeClientContactName('clientContactLastName')"
                placeholder="Last Name"
                variant="outlined"
                density="compact"
                hide-details
                autocomplete="new-password"
                spellcheck="false"
                :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-text-field
            v-model="formData.clientContactEmail"
            placeholder="Email Address"
            variant="outlined"
            density="compact"
            type="email"
            hide-details
            class="mt-2"
            autocomplete="new-password"
            spellcheck="false"
            :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
            @blur="validateEmail(formData.clientContactEmail)"
            @input="validateEmail(formData.clientContactEmail)"
            :class="{ 'field-error': formErrors.clientContactEmail }"
          ></v-text-field>
          <!-- Email Error Message -->
          <div v-if="formErrors.clientContactEmail" class="error-message mt-1">
            {{ formErrors.clientContactEmail }}
          </div>
        </div>
      </div>

      <!-- Primary Valuer & Job Originator Section -->
      <div class="form-field">
        <div class="forward-primary-banner">
          <span class="forward-primary-label">Primary Valuer:</span>
          <span class="forward-primary-name">
            {{ selectedAssignment?.primaryAppraiser || 'N/A' }}
          </span>
        </div>
        <div class="forward-primary-banner">
          <span class="forward-primary-label">Job Originator:</span>
          <span class="forward-primary-name">
            {{ selectedAssignment?.originator || 'N/A' }}
          </span>
        </div>
      </div>

      <!-- CC Recipients Section - Only show when NOT replying to selected email -->
      <AdditionalRecipientsStandalone 
        v-if="!formData.replyToSelectedEmail"
        ref="recipientsComponent"
        :showDescription="false"
        :ccOptionsConfig="{ includeOriginator: false, includeValuers: true, includeValuationTeam: true, includePrimaryValuer: true }"
        @selection-changed="handleSelectionChanged"
      />

      <!-- Signature Field -->
      <div class="form-field">
        <label class="field-label">Signature*</label>
        <div class="signature-container">
          <!-- Vue3 Rich Text Editor -->
          <VueEditor
            ref="signatureEditor"
            key="signatureEditor"
            class="signature-vue-editor"
            v-model="formData.signature"
            :editorToolbar="customToolbar"
            :editorOptions="editorOptions"
            placeholder="Enter your signature here..."
            @ready="onEditorReady"
            @input="onSignatureInput"
            @text-change="onSignatureTextChange"
          />
          
          <!-- Character Count -->
          <div class="signature-char-count" :class="{ error: signatureLength > 500 }">
            {{ signatureLength }}/500 characters
          </div>
          
          <!-- Error Message -->
          <div v-if="formErrors.signature" class="error-message">
            {{ formErrors.signature }}
          </div>
        </div>
      </div>

    </div> <!-- End form-disabled wrapper -->

  </div> <!-- End client-introduction-wrapper -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SelectedEmailAndAssignment from '../commons/SelectedEmailAndAssignment.vue';
import IsLoadingComponent from '../IsLoadingComponent.vue';
import IntroParagraphSelector from '../common/IntroParagraphSelector.vue';
import AdditionalRecipientsStandalone from './AdditionalRecipientsStandalone.vue';
import { VueEditor } from 'vue3-editor';
import { clearAssignmentCache, handleRecentActions } from '../../service/engagement.js';
import { generateEmailTemplate } from '../../service/emailTemplates.js';
import { getGraphToken } from '../../service/TokenService.js';
import { getPropertyAddressOrName } from '../../utils/globalUtil.js';
import { ACTION_TYPES } from '../../constants.js';
import { markEmailAsCompleteNew } from '../../service/Actions.js';

export default {
  name: 'ClientIntroduction',
  components: {
    SelectedEmailAndAssignment,
    IsLoadingComponent,
    IntroParagraphSelector,
    AdditionalRecipientsStandalone,
    VueEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    actionData: {
      type: Object,
      default: () => ({})
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        replyToSelectedEmail: false,
        selectedIntroParagraph: 'default',
        selectedIntroParagraphObject: null,
        clientContactFirstName: '',
        clientContactLastName: '',
        clientContactEmail: '',
        signature: ''
      },
      originalClientContact: {
        firstName: '',
        lastName: '',
        email: ''
      },
      clientData: null,
      existingContactData: null,
      formErrors: {
        clientContactEmail: '',
        signature: ''
      },
      showLoading: false,
      isLoadingPropertyContacts: false,
      isLoadingSignature: false,
      isEditorReady: false,
      selectedCCEmails: [],
      
      isSignatureFocused: false,
      
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ],
      
      editorOptions: {
        debug: false,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
          ],
          history: {
            delay: 1000,
            maxStack: 50,
            userOnly: false
          },
          clipboard: {
            matchVisual: false
          }
        },
        theme: 'snow',
        strict: false,
        scrollingContainer: null,
        bounds: null,
        readOnly: false,
        placeholder: 'Enter your signature here...',
        formats: ['bold', 'italic', 'underline', 'link', 'list']
      },
      
      processingStartTime: null,
      emailContentSubject: null,
    };
  },
  computed: {
    ...mapGetters({
      selectedAssignment: 'assignments/selectedAssignment',
      emailData: 'email/currentEmail',
      assignmentOverviewData: 'assignments/assignmentOverviewData',
      getUserDetails: "email/getUserDetails",
      isLoadingAssignmentData: 'assignments/isLoading',
    }),

    hasClientGovernanceAccess() {
      return this.getUserDetails?.clientGovernanceAccess === true;
    },

    loggedInUserEmail() {
      return (
        this.getUserDetails?.emailAddress ||
        Office.context.mailbox?.userProfile?.emailAddress ||
        ''
      ).toLowerCase().trim();
    },

   isLoggedInUserJO() {
      if (!this.loggedInUserEmail || !this.selectedAssignment?.originatorEmail) return false;
      return this.loggedInUserEmail === this.selectedAssignment.originatorEmail.toLowerCase().trim();
    },

    // Parse and cache client reference IDs
    parsedClientReference() {
      try {
        const clientReferenceId = this.selectedAssignment?.clientDetails?.clientReferenceId;
        if (!clientReferenceId) return null;

        const clientRefMatch = clientReferenceId.match(/^(COMP#.+?)-(CLNT#.+)$/);
        if (!clientRefMatch) return null;

        return {
          clientCompanyId: clientRefMatch[1],
          clientId: clientRefMatch[2]
        };
      } catch (error) {
        console.error('Error parsing clientReferenceId:', error);
        return null;
      }
    },

    // Parse and cache contact reference IDs
    parsedContactReference() {
      try {
        const contactReferenceId = this.selectedAssignment?.clientDetails?.contactReferenceId;
        if (!contactReferenceId) return null;

        const contactRefMatch = contactReferenceId.match(/^(OFC#.+?)-(CONTACT#.+)$/);
        if (!contactRefMatch) return null;

        return {
          clientOfficeId: contactRefMatch[1],
          clientContactId: contactRefMatch[2]
        };
      } catch (error) {
        console.error('Error parsing contactReferenceId:', error);
        return null;
      }
    },

    isFormValid() {
      const { clientContactFirstName, clientContactLastName, clientContactEmail, signature } = this.formData;
      
      const nameValid = clientContactFirstName?.trim() && clientContactLastName?.trim();
      const emailValid = clientContactEmail?.trim() && this.isValidEmail(clientContactEmail);
      
      let signatureValid = false;
      if (signature && signature.trim()) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = signature;
        const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        signatureValid = textContent.length > 0;
      }
      
      const noErrors = !this.formErrors.clientContactEmail && !this.formErrors.signature;
      
      return !!(nameValid && emailValid && signatureValid && noErrors);
    },

    hasEmailForReply() {
      return !!(this.emailData && this.emailData.subject && this.emailData.subject.trim());
    },

    signatureLength() {
      if (!this.formData.signature) return 0;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.formData.signature || '';
      const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
      return textContent.length;
    }
  },
  watch: {
    isFormValid(newValue) {
      this.$emit('isValidForSubmit', newValue);
    },
    assignmentOverviewData: {
      handler(newData) {
        if (newData) {
          this.populateFormFields(newData);
        } else {
          this.clearFormFields();
        }
      },
      immediate: true,
      deep: true
    },
    formData: {
      handler() {
        this.$nextTick(() => {
          this.$emit('isValidForSubmit', this.isFormValid);
        });
      },
      deep: true
    },
    visible: {
      async handler(newValue, oldValue) {
        if (newValue && !oldValue) {
          this.isLoadingPropertyContacts = true;
          try {
            if (this.selectedAssignment && this.selectedAssignment.viewEventId) {
              clearAssignmentCache(this.selectedAssignment.viewEventId);
              await this.$store.dispatch('assignments/fetchAssignmentOverviewData', this.selectedAssignment.viewEventId);
              await this.$nextTick();
              const freshData = this.$store.getters['assignments/assignmentOverviewData'];
              if (freshData) {
                this.populateFormFields(freshData);
              }
              
              await this.fetchAndStoreContactData();
            }
          } catch (error) {
            console.error('❌ Failed to fetch latest assignment data on dialog open:', error);
            this.$emit('showNotification', {
              type: 'warning',
              message: 'Failed to load latest assignment data. Using cached information.'
            });
          } finally {
            this.isLoadingPropertyContacts = false;
          }
        }
      }
    },
    emailData: {
      immediate: true,
      handler(newEmail, oldEmail) {
        if (newEmail !== oldEmail) {
          this.formData.replyToSelectedEmail = false;
          if (oldEmail !== undefined) {
            this.$emit('close');
          }
        }
      }
    }
  },
  async mounted() {
    this.processingStartTime = performance.now();
    
    this.$nextTick(() => {
      const originalConsoleError = console.error;
      console.error = (...args) => {
        const errorString = args.join(' ').toLowerCase();
        if (
          errorString.includes('cannot read properties of undefined') ||
          errorString.includes('mutationobserver') ||
          errorString.includes('scroll.update') ||
          errorString.includes('reading \'emit\'') ||
          errorString.includes('quill') ||
          errorString.includes('leaf.position is not a function') ||
          errorString.includes('failed to execute \'setstart\' on \'range\'') ||
          errorString.includes('indexsizeerror')
        ) {
          return;
        }
        originalConsoleError.apply(console, args);
      };
    });
    
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      const errorString = (message || '').toLowerCase();
      if (
        errorString.includes('cannot read properties of undefined') ||
        errorString.includes('reading \'emit\'') ||
        errorString.includes('mutationobserver') ||
        (source && source.includes('taskpane.js'))
      ) {
        return true;
      }
      if (originalOnError) {
        return originalOnError.call(window, message, source, lineno, colno, error);
      }
      return false;
    };

    window.addEventListener('unhandledrejection', (event) => {
      const errorString = (event.reason?.message || event.reason || '').toLowerCase();
      if (
        errorString.includes('cannot read properties of undefined') ||
        errorString.includes('reading \'emit\'') ||
        errorString.includes('mutationobserver') ||
        errorString.includes('failed to execute \'setstart\' on \'range\'') ||
        errorString.includes('indexsizeerror') ||
        errorString.includes('leaf.position is not a function')
      ) {
        event.preventDefault();
      }
    });
    
    const originalMutationObserver = window.MutationObserver;
    window.MutationObserver = function(callback) {
      const wrappedCallback = function(mutations, observer) {
        try {
          return callback.call(this, mutations, observer);
        } catch (error) {
          return;
        }
      };
      return new originalMutationObserver(wrappedCallback);
    };
    
    Object.setPrototypeOf(window.MutationObserver, originalMutationObserver);
    Object.getOwnPropertyNames(originalMutationObserver).forEach(name => {
      if (typeof originalMutationObserver[name] !== 'function') {
        Object.defineProperty(window.MutationObserver, name, 
          Object.getOwnPropertyDescriptor(originalMutationObserver, name));
      }
    });
    
    await this.$nextTick();
    
    setTimeout(async () => {
      try {
        let retries = 0;
        while (!this.isEditorReady && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 200));
          retries++;
        }
        await this.loadSignatureFromUserDetails();
      } catch (error) {
        console.error('❌ Error in signature loading process:', error);
        await this.createBasicSignature();
        this.$emit('showNotification', {
          type: 'warning',
          message: 'Could not load signature automatically. Using basic signature.'
        });
      }
    }, 2500);
    
    this.$nextTick(async () => {
      // Wait for child components to initialize
      await this.$nextTick();
      
      // Always reset intro paragraph selection to default when popup opens
      this.formData.selectedIntroParagraph = 'default';
      this.formData.selectedIntroParagraphObject = null;
      
      this.$emit('isValidForSubmit', this.isFormValid);
    });
  },
  methods: {
    handleSelectionChanged({ selectedEmails }) {
      this.selectedCCEmails = selectedEmails;
    },

    normalizeNameCase(value) {
      const trimmedValue = (value || '').trim();
      if (!trimmedValue) {
        return '';
      }

      return trimmedValue
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) =>
          part
            .split(/\s+/)
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        )
        .join(', ');
    },

    normalizeClientContactName(fieldName) {
      if (!['clientContactFirstName', 'clientContactLastName'].includes(fieldName)) {
        return;
      }

      this.formData[fieldName] = this.normalizeNameCase(this.formData[fieldName]);
    },

    onIntroParagraphChanged(paragraph) {
      this.formData.selectedIntroParagraphObject = paragraph;
    },

    handleIntroParagraphError(message) {
      console.error('❌ Intro paragraph error:', message);
      this.$emit('showNotification', {
        type: 'error',
        message: message || 'Error with intro paragraph'
      });
    },

    handleIntroParagraphSuccess(message) {
      if (message) {
        this.$emit('showNotification', {
          type: 'success',
          message: message
        });
      }
    },

    onEditorReady(quill) {
      try {
        this.isEditorReady = true;
        
        if (quill) {
          const originalGetBounds = quill.selection?.getBounds;
          if (originalGetBounds) {
            quill.selection.getBounds = function(index, length) {
              try {
                return originalGetBounds.call(this, index, length);
              } catch (error) {
                return { bottom: 0, height: 20, left: 0, right: 0, top: 0, width: 0 };
              }
            };
          }
        }
      } catch (error) {
        this.isEditorReady = true;
      }
    },

    onSignatureInput(content) {
      try {
        this.formData.signature = content || '';
        
        if (content && content.trim()) {
          this.saveSignatureForFutureUse(content);
        }
        
        if (content && content.trim()) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = content;
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent.length === 0) {
            this.formErrors.signature = 'Signature cannot be empty';
          } else if (this.signatureLength > 500) {
            this.formErrors.signature = 'Signature is too long. Please keep it under 500 characters.';
          } else {
            this.formErrors.signature = '';
          }
        } else {
          this.formErrors.signature = 'Signature is required';
        }
      } catch (error) {
        if (content) {
          this.formData.signature = content;
        }
      }
    },

    onSignatureTextChange() {
      try {
        if (this.formData.signature && this.formData.signature.trim()) {
          this.saveSignatureForFutureUse(this.formData.signature);
        }
        
        if (this.formData.signature && this.formData.signature.trim()) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.formData.signature;
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent.length === 0) {
            this.formErrors.signature = 'Signature cannot be empty';
          } else if (this.signatureLength > 500) {
            this.formErrors.signature = 'Signature is too long. Please keep it under 500 characters.';
          } else {
            this.formErrors.signature = '';
          }
        } else {
          this.formErrors.signature = 'Signature is required';
        }
      } catch (error) {
        this.formErrors.signature = '';
      }
    },

    safeUpdateSignature(content) {
      try {
        if (!content || content.trim() === '') {
          if (content === '') {
            this.formData.signature = '';
          }
          return;
        }
        
        this.formData.signature = content;
        this.ensureSignatureDisplayed();
      } catch (error) {
        if (content) {
          this.formData.signature = content;
          this.ensureSignatureDisplayed();
        }
      }
    },

    async loadSignatureFromUserDetails() {
      this.isLoadingSignature = true;
      try {
        const roamingSuccess = await this.loadSignatureFromRoamingSettings();
        if (roamingSuccess && this.formData.signature) {
          this.ensureSignatureDisplayed();
          this.isLoadingSignature = false;
          return;
        }
        
        const userEmail = Office.context.mailbox?.userProfile?.emailAddress;
        
        if (userEmail) {
          try {
            const { fetchUserDetails } = await import('../../service/engagement.js');
            const userDetailsFromAPI = await fetchUserDetails(userEmail);
            
            if (userDetailsFromAPI) {
              const signatureHtml = await this.createSignatureFromAPIData(userDetailsFromAPI);
              this.safeUpdateSignature(signatureHtml);
              
              await this.saveSignatureForFutureUse(signatureHtml);
              this.ensureSignatureDisplayed();
              this.isLoadingSignature = false;
              return;
            }
          } catch (apiError) {
            console.warn('⚠️ Could not fetch user details from API:', apiError.message);
          }
        }
        
        const localSuccess = await this.loadSignatureFromLocalStorage();
        if (localSuccess && this.formData.signature) {
          await this.saveSignatureForFutureUse(this.formData.signature);
          this.ensureSignatureDisplayed();
          this.isLoadingSignature = false;
          return;
        }
        
        await this.createBasicSignature();
        this.ensureSignatureDisplayed();
        
      } catch (error) {
        this.$emit('showNotification', {
          type: 'warning',
          message: 'Could not load signature. Using basic signature.'
        });
        await this.createBasicSignature();
        this.ensureSignatureDisplayed();
      } finally {
        this.isLoadingSignature = false;
      }
    },

    ensureSignatureDisplayed() {
      this.$nextTick(() => {
        try {
          const editor = this.$refs.signatureEditor;
          if (editor && editor.quill && this.formData.signature) {
            const currentContent = editor.quill.root.innerHTML;
            const expectedContent = this.formData.signature;
            
            if (currentContent !== expectedContent) {
              editor.quill.clipboard.dangerouslyPasteHTML(expectedContent);
              
              if (editor.quill.root.innerHTML !== expectedContent) {
                editor.quill.root.innerHTML = expectedContent;
              }
            }
          }
        } catch (error) {
          try {
            const editor = this.$refs.signatureEditor;
            if (editor && editor.$el) {
              const editorDiv = editor.$el.querySelector('.ql-editor');
              if (editorDiv && this.formData.signature) {
                editorDiv.innerHTML = this.formData.signature;
              }
            }
          } catch (fallbackError) {
            console.error('Fallback signature display also failed:', fallbackError.message);
          }
        }
      });
    },

    preserveRichTextFormatting(signatureHtml) {
      // If it's plain text, just add br tags
      if (!signatureHtml.includes('<') && !signatureHtml.includes('>')) {
        return signatureHtml.replace(/\n/g, '<br>');
      }

      // Clean up the HTML while preserving all formatting tags
      let cleaned = signatureHtml
        // First, handle div to div transitions with br tags
        .replace(/<\/div>\s*<div[^>]*>/gi, '</div><br><div>')
        // Then remove all div tags but keep the br tags we just added
        .replace(/<div[^>]*>/gi, '')
        .replace(/<\/div>/gi, '')
        // Handle p tags the same way
        .replace(/<\/p>\s*<p[^>]*>/gi, '</p><br><p>')
        .replace(/<p[^>]*>/gi, '')
        .replace(/<\/p>/gi, '')
        // Convert any remaining newlines to br tags
        .replace(/\n/g, '<br>')
        // Clean up multiple consecutive br tags
        .replace(/(<br>\s*){2,}/gi, '<br>')
        // Remove trailing br
        .replace(/<br>\s*$/, '')
        // Clean up whitespace
        .replace(/\s+/g, ' ')
        .trim();

      return cleaned;
    },

    async createSignatureFromAPIData(userDetails) {
      const name = userDetails.fullName || userDetails.displayName || '[Name]';
      const title = userDetails.jobTitle || userDetails.title || '[Your Title]';
      let role = userDetails.role || '[Role]';
      const division = userDetails.divisionName || '[Division]';
      const phone = userDetails.phoneNumber  || '[Phone]';
      const email = userDetails.emailAddress || '[Email]';
      
      if (userDetails.viewUserRoleId) {
        try {
          const { fetchUserRoleDetails } = await import('../../service/engagement.js');
          const roleDetails = await fetchUserRoleDetails(userDetails.viewUserRoleId);
          if (roleDetails && roleDetails.viewUserRole) {
            role = roleDetails.viewUserRole;
          }
        } catch (error) {
          console.warn('Could not fetch role details:', error.message);
        }
      }
      
      const officeAddress = userDetails?.officeAddress || userDetails?.address || '[Office Address, City, State, Zipcode]';
      const formattedPhone = (phone !== '[Phone]' && phone && !phone.startsWith('T ')) ? `T ${phone}` : phone;
      
      return `${name}
${title}
${role} | ${division}
${officeAddress}
${formattedPhone}
${email}`;
    },

    async createBasicSignature() {
      try {
        const userEmail = Office.context.mailbox?.userProfile?.emailAddress;
        
        if (userEmail) {
          const { fetchUserDetails } = await import('../../service/engagement.js');
          const userDetailsFromAPI = await fetchUserDetails(userEmail);
          
          if (userDetailsFromAPI) {
            const signatureHtml = await this.createSignatureFromAPIData(userDetailsFromAPI);
            this.safeUpdateSignature(signatureHtml);
            return;
          }
        }
        
        this.safeUpdateSignature('');
      } catch (error) {
        this.safeUpdateSignature('');
      }
    },

    async loadSignatureFromRoamingSettings() {
      try {
        if (Office.context.roamingSettings) {
          const savedSignature = Office.context.roamingSettings.get('userSignature');
          if (savedSignature && savedSignature.trim()) {
            this.safeUpdateSignature(savedSignature);
            await this.$nextTick();
            if (this.formData.signature && this.formData.signature.trim()) {
              return true;
            }
          }
        }
      } catch (error) {
        console.warn('Could not load signature from roaming settings:', error.message);
      }
      return false;
    },

    async loadSignatureFromLocalStorage() {
      try {
        const userEmail = this.getUserDetails?.emailAddress || Office.context.mailbox?.userProfile?.emailAddress;
        if (userEmail) {
          const storageKey = `outlookSignature_${userEmail}`;
          const savedSignature = localStorage.getItem(storageKey);
          if (savedSignature) {
            this.safeUpdateSignature(savedSignature);
            await this.$nextTick();
            if (this.formData.signature && this.formData.signature.trim()) {
              return true;
            }
          }
        }
      } catch (error) {
        console.warn('Could not load signature from localStorage:', error);
      }
      return false;
    },

    async saveSignatureForFutureUse(signature) {
      try {
        const userEmail = this.getUserDetails?.emailAddress || Office.context.mailbox?.userProfile?.emailAddress;
        
        if (Office.context.roamingSettings) {
          Office.context.roamingSettings.set('userSignature', signature);
          Office.context.roamingSettings.saveAsync();
        }

        if (userEmail) {
          const storageKey = `outlookSignature_${userEmail}`;
          localStorage.setItem(storageKey, signature);
        }
      } catch (error) {
        console.warn('Could not save signature:', error);
      }
    },

    ...mapActions({ 
      addMetricsData: "assignments/addMetricsData"
    }),

    hasClientContactChanged() {
      const currentFirstName = (this.formData.clientContactFirstName || '').trim();
      const currentLastName = (this.formData.clientContactLastName || '').trim();
      const currentEmail = (this.formData.clientContactEmail || '').trim();
      
      const originalFirstName = (this.originalClientContact.firstName || '').trim();
      const originalLastName = (this.originalClientContact.lastName || '').trim();
      const originalEmail = (this.originalClientContact.email || '').trim();
      
      const nameChanged = currentFirstName !== originalFirstName || currentLastName !== originalLastName;
      const emailChanged = currentEmail !== originalEmail;
      
      return nameChanged || emailChanged;
    },

    async fetchClientHierarchy(clientCompanyId) {
      try {
        if (!clientCompanyId) {
          console.error('❌ No client company ID provided');
          return null;
        }

        const token = await import('../../service/TokenService').then(m => m.getTops2Token());
        const encodedCompanyId = encodeURIComponent(clientCompanyId);
        const url = `${process.env.VITE_CLIENTS_OL_BASE_URL}/clientHierarchy/changes/${encodedCompanyId}?updatesRequired=false`;
        
        console.log('🔄 Fetching client hierarchy for:', clientCompanyId);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Azuretoken': token
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Client hierarchy data fetched:', data);
        
        this.clientData = data;
        return data;
        
      } catch (error) {
        console.error('❌ Error fetching client hierarchy:', error);
        return null;
      }
    },

    async fetchAndStoreContactData() {
      try {
        // Use cached parsed IDs from computed properties
        const clientRef = this.parsedClientReference;
        const contactRef = this.parsedContactReference;

        if (!clientRef || !contactRef) {
          console.error('❌ Could not parse client or contact reference IDs');
          return;
        }

        const { clientCompanyId, clientId } = clientRef;
        const { clientOfficeId, clientContactId } = contactRef;        const hierarchyData = await this.fetchClientHierarchy(clientCompanyId);
                
        // Extract the specific contact data from hierarchy
        let contactData = null;
        
        if (hierarchyData.clientCompany?.clients?.length > 0) {
          const matchingClient = hierarchyData.clientCompany.clients.find(client => 
            client.clientId === clientId || client.clientId.endsWith(clientId)
          );
          
          if (matchingClient?.clientOffices?.length > 0) {
            const matchingOffice = matchingClient.clientOffices.find(office => 
              office.clientOfficeId === clientOfficeId || office.clientOfficeId.endsWith(clientOfficeId)
            );
            
            if (matchingOffice?.clientContacts?.length > 0) {
              contactData = matchingOffice.clientContacts.find(contact => 
                contact.clientContactId === clientContactId || contact.clientContactId.endsWith(clientContactId)
              );
            }
          }
        }
        
        if (contactData) {
          this.existingContactData = contactData;
          console.log('✅ Contact data stored for future updates:', contactData);
        } else {
          console.warn('⚠️ Could not find contact in client hierarchy');
        }
        
      } catch (error) {
        console.error('❌ Error fetching and storing contact data:', error);
      }
    },

    extractContactFromHierarchy(hierarchyData) {
      if (!hierarchyData) {
        console.warn('⚠️ extractContactFromHierarchy: hierarchyData is null or undefined');
        return null;
      }

      try {
        console.log('🔍 Extracting contact from hierarchy. Full structure:', JSON.stringify(hierarchyData, null, 2));
        
        // Get the client IDs from assignmentOverviewData for matching
        const clientDetails = this.assignmentOverviewData?.clientDetails;
        const targetClientId = clientDetails?.clientPk;
        const targetOfficeId = clientDetails?.clientOfficePk;
        const targetContactId = clientDetails?.clientContactPk;
        
        console.log('🎯 Target IDs from assignmentOverview:', {
          targetClientId,
          targetOfficeId,
          targetContactId
        });
        
        let firstName = '';
        let lastName = '';
        let email = '';
        
        // Path 1: Navigate through clientCompany.clients and match by clientId
        if (hierarchyData.clientCompany?.clients?.length > 0) {
          // Find the matching client
          const matchingClient = hierarchyData.clientCompany.clients.find(client => 
            client.clientId === targetClientId || client.clientId === `CLNT#${targetClientId}`
          );
          
          if (matchingClient) {
            console.log('✅ Found matching client:', matchingClient.clientId);
            
            if (matchingClient.clientOffices?.length > 0) {
              // Find the matching office
              const matchingOffice = matchingClient.clientOffices.find(office => 
                office.clientOfficeId === targetOfficeId || office.clientOfficeId === `OFC#${targetOfficeId}`
              );
              
              if (matchingOffice) {
                console.log('✅ Found matching office:', matchingOffice.clientOfficeId);
                
                if (matchingOffice.clientContacts?.length > 0) {
                  // Find the matching contact
                  const matchingContact = matchingOffice.clientContacts.find(contact => 
                    contact.clientContactId === targetContactId || contact.clientContactId === `CONTACT#${targetContactId}`
                  );
                  
                  if (matchingContact) {
                    console.log('✅ Found matching contact:', matchingContact.clientContactId);
                    firstName = matchingContact.firstName || '';
                    lastName = matchingContact.lastName || '';
                    email = matchingContact.emailId || matchingContact.email || matchingContact.emailAddress || '';
                  } else {
                    console.warn('⚠️ No matching contact found, using first contact');
                    const contact = matchingOffice.clientContacts[0];
                    firstName = contact.firstName || '';
                    lastName = contact.lastName || '';
                    email = contact.emailId || contact.email || contact.emailAddress || '';
                  }
                }
              } else {
                console.warn('⚠️ No matching office found, using first office');
                const office = matchingClient.clientOffices[0];
                if (office.clientContacts?.length > 0) {
                  const contact = office.clientContacts[0];
                  firstName = contact.firstName || '';
                  lastName = contact.lastName || '';
                  email = contact.emailId || contact.email || contact.emailAddress || '';
                }
              }
            }
          } else {
            console.warn('⚠️ No matching client found, using first client');
            const client = hierarchyData.clientCompany.clients[0];
            if (client.clientOffices?.length > 0) {
              const office = client.clientOffices[0];
              if (office.clientContacts?.length > 0) {
                const contact = office.clientContacts[0];
                firstName = contact.firstName || '';
                lastName = contact.lastName || '';
                email = contact.emailId || contact.email || contact.emailAddress || '';
              }
            }
          }
        }
        
        // Path 2: Direct properties on hierarchyData (fallback)
        if (!firstName && !lastName && !email) {
          if (hierarchyData.firstName || hierarchyData.lastName || hierarchyData.email) {
            console.log('✅ Found contact data directly on hierarchyData');
            firstName = hierarchyData.firstName || '';
            lastName = hierarchyData.lastName || '';
            email = hierarchyData.email || hierarchyData.emailAddress || '';
          }
        }
        
        if (!firstName && !lastName && !email) {
          console.warn('⚠️ Could not find contact data in any expected path');
          console.log('Available keys:', Object.keys(hierarchyData));
          return null;
        }

        // Trim values
        firstName = firstName.trim();
        lastName = lastName.trim();
        email = email.trim();

        // If no data found, return null
        if (!firstName && !lastName && !email) {
          return null;
        }

        console.log('✅ Successfully extracted contact:', { firstName, lastName, email });
        return { firstName, lastName, email };
        
      } catch (error) {
        console.error('❌ Error in extractContactFromHierarchy:', error.message, error.stack);
        return null;
      }
    },

    async updateClientContact() {
      try {
        const hasChanged = this.hasClientContactChanged();
        if (!hasChanged) {
          console.log('ℹ️ No changes to update');
          return true;
        }
        
        // Use pre-fetched contact data if available, otherwise fetch it
        let contactData = this.existingContactData;
        
        if (!contactData) {
          console.warn('⚠️ Contact data not pre-fetched, fetching now...');
          await this.fetchAndStoreContactData();
          contactData = this.existingContactData;
        }
        
        if (!contactData) {
          throw new Error('Could not fetch contact data');
        }
                
        // Use cached parsed IDs from computed properties
        const clientRef = this.parsedClientReference;
        const contactRef = this.parsedContactReference;

        if (!clientRef || !contactRef) {
          throw new Error('Could not parse client or contact reference IDs');
        }

        const { clientCompanyId, clientId } = clientRef;
        const { clientOfficeId, clientContactId } = contactRef;
        
        
        // Get current user email for createdBy/updatedBy
        const userEmail = this.getUserDetails?.emailAddress || Office.context.mailbox?.userProfile?.emailAddress;

        // Check what changed
        const currentFirstName = (this.formData.clientContactFirstName || '').trim();
        const currentLastName = (this.formData.clientContactLastName || '').trim();
        const currentEmail = (this.formData.clientContactEmail || '').trim();
        
        const originalFirstName = (this.originalClientContact.firstName || '').trim();
        const originalLastName = (this.originalClientContact.lastName || '').trim();
        const originalEmail = (this.originalClientContact.email || '').trim();
        
        const nameChanged = currentFirstName !== originalFirstName || currentLastName !== originalLastName;
        const emailChanged = currentEmail !== originalEmail;

        // Build the request body
        const requestBody = {
          communicationChannel: {
            email: [
              {
                emailId: this.formData.clientContactEmail.trim(),
                emailIdUsage: "primary"
              }
            ],
            phone: [
              {
                phoneNumber: contactData?.phoneNumber || null,
                phoneType: "WORK"
              }
            ],
            fax: [
              {
                phoneNumber: contactData?.faxNumber || null,
                phoneType: "WORK"
              }
            ],
            createdBy: contactData?.createdBy || userEmail,
            updatedBy: userEmail
          },
          lastName: this.formData.clientContactLastName.trim() || '',
          updatedBy: userEmail,
          fullName: `${this.formData.clientContactFirstName.trim()} ${contactData?.middleName || ''} ${this.formData.clientContactLastName.trim()}`.replace(/\s+/g, ' ').trim(),
          middleName: contactData?.middleName || null,
          isActive: contactData?.isActive !== undefined ? contactData.isActive : true,
          firstName: this.formData.clientContactFirstName.trim(),
          createdBy: contactData?.createdBy || userEmail,
          title: contactData?.title || null,
          salutation: contactData?.salutation || null,
          notes: contactData?.notes || null,
          emailUpdated: emailChanged,
          phoneUpdated: false,
          isPendingVerification: contactData?.isPendingVerification
        };

        const token = await import('../../service/TokenService').then(m => m.getTops2Token());
        const encodedCompanyId = encodeURIComponent(clientCompanyId);
        const encodedClientId = encodeURIComponent(clientId);
        const encodedOfficeId = encodeURIComponent(clientOfficeId);
        const encodedContactId = encodeURIComponent(clientContactId);
        
        const putUrl = `${process.env.VITE_CLIENTS_OL_BASE_URL}/clientCompanies/${encodedCompanyId}/clients/${encodedClientId}/clientOffices/${encodedOfficeId}/clientContacts/${encodedContactId}?duplicateAllowed=false`;

        const response = await fetch(putUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Azuretoken': token
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ PUT request failed:', response.status, errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        console.log('✅ Client contact updated successfully:', responseData);

        // Update original values to reflect the successful update
        this.originalClientContact = {
          firstName: this.formData.clientContactFirstName,
          lastName: this.formData.clientContactLastName,
          email: this.formData.clientContactEmail
        };

        return true;

      } catch (error) {
        console.error('❌ Error updating client contact:', error);
        this.$emit('showNotification', {
          type: 'error',
          message: 'Failed to update client contact information. Please try again.'
        });
        return false;
      }
    },

    async handleAssignmentChanged(assignment) {
      this.$emit('assignmentChanged', assignment);
      
      this.clearFormFields();
      
      if (assignment && assignment.viewEventId) {
        this.isLoadingPropertyContacts = true;
        try {
          clearAssignmentCache(assignment.viewEventId);
          await this.$store.dispatch('assignments/fetchAssignmentOverviewData', assignment.viewEventId);
          
          await this.$nextTick();
          const freshData = this.$store.getters['assignments/assignmentOverviewData'];
          
          if (freshData) {
            this.populateFormFields(freshData);
          }
        } catch (error) {
          console.error('❌ Failed to fetch assignment overview data:', error);
          this.$emit('showNotification', {
            type: 'error',
            message: 'Failed to load assignment data. Please try again.'
          });
        } finally {
          this.isLoadingPropertyContacts = false;
        }
      }
    },

    clearFormFields() {
      this.formData.replyToSelectedEmail = false;
      this.formData.clientContactFirstName = '';
      this.formData.clientContactLastName = '';
      this.formData.clientContactEmail = '';
    },

    async populateFormFields(overviewData) {
      if (!overviewData) {
        return;
      }
      
      const clientDetails = overviewData.clientDetails;
      
      if (!clientDetails) {
        return;
      }

      const client = this.selectedAssignment.clientDetails;
      
      // Get first name and last name from search result
      let firstName = client.firstName || '';
      let lastName = client.lastName || '';
      
      // If firstName doesn't exist but we have a fullName or clientName, split it
      if (!firstName && (client.fullName || client.clientName)) {
        const fullName = (client.fullName || client.clientName || '').trim();
        if (fullName) {
          const nameParts = fullName.split(/\s+/); // Split by whitespace
          if (nameParts.length > 0) {
            firstName = nameParts[0]; // First word is first name
            lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''; // Last word is last name
          }
        }
      }

      const normalizedFirstName = this.normalizeNameCase(firstName);
      const normalizedLastName = this.normalizeNameCase(lastName);
      
      this.formData.clientContactFirstName = normalizedFirstName;
      this.formData.clientContactLastName = normalizedLastName;
      
      // Get email from assignment overview clientDetails
      this.formData.clientContactEmail = clientDetails.clientContactEmail || '';
      
      // Store original values for change detection
      this.originalClientContact = {
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        email: clientDetails.clientContactEmail || ''
      };
      
    },

    validateEmail(email) {
      if (!email) {
        this.formErrors.clientContactEmail = '';
        return;
      }

      if (!this.isValidEmail(email)) {
        this.formErrors.clientContactEmail = 'Please enter a valid email address.';
      } else {
        this.formErrors.clientContactEmail = '';
      }
    },

    isValidEmail(email) {
      if (!email || typeof email !== 'string') {
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    },

    async startTemplate() {
      this.showLoading = true;

      try {
        if (this.formData.clientContactEmail) {
          this.validateEmail(this.formData.clientContactEmail);
        }

        if (!this.formData.signature || !this.formData.signature.trim()) {
          this.formErrors.signature = 'Signature is required';
        } else {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.formData.signature;
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent.length === 0) {
            this.formErrors.signature = 'Signature cannot be empty';
          } else if (this.signatureLength > 500) {
            this.formErrors.signature = 'Signature is too long. Please keep it under 500 characters.';
          } else {
            this.formErrors.signature = '';
          }
        }

        if (!this.isFormValid) {
          let errorMessage = 'Please fill in all required fields';
          
          if (!this.formData.clientContactFirstName) {
            errorMessage = 'Please enter Client Contact first name';
          } else if (!this.formData.clientContactLastName) {
            errorMessage = 'Please enter Client Contact last name';
          } else if (!this.formData.clientContactEmail) {
            errorMessage = 'Please enter Client Contact email address';
          } else if (this.formErrors.clientContactEmail) {
            errorMessage = this.formErrors.clientContactEmail;
          } else if (!this.formData.signature || !this.formData.signature.trim()) {
            errorMessage = 'Please enter your signature';
          } else if (this.formErrors.signature) {
            errorMessage = this.formErrors.signature;
          }
          
          this.$emit('showNotification', {
            type: 'error',
            message: errorMessage
          });
          return;
        }

        // Update client contact if changes were detected and user has governance access
        const hasChanged = this.hasClientContactChanged();
        if (hasChanged) {
          if (!this.hasClientGovernanceAccess) {
            console.info('⚠️ Client contact update skipped: User does not have client governance access');
            this.$emit('showNotification', {
              type: 'info',
              message: 'Client contact changes not saved: You do not have permission to update client information.'
            });
          } else {
            await this.updateClientContact();
          }
        }
        
        // Generate and create email with Client Introduction template
        let finalStatus = 'Completed';
        let errorMessage = null;
        let emailSubject = '';

        try {
          emailSubject = await this.createClientIntroductionEmail();
        } catch (emailError) {
          finalStatus = 'Error';
          errorMessage = emailError.message;
          console.error('❌ Error creating Client Introduction email:', emailError);
          this.$emit('showNotification', {
            type: 'error',
            message: `Failed to create email: ${emailError.message}`
          });
        } finally {
          // Calculate processing time and save metrics
          const endTime = performance.now();
          const processingTime = Math.round(endTime - this.processingStartTime);

          const metricsPayload = {
            entityId: this.selectedAssignment?.viewEventId,
            fileId: this.selectedAssignment?.code,
            actionTaken: ACTION_TYPES.CLIENT_INTRODUCTION,
            status: finalStatus,
            subjectLine: emailSubject,
            processingTime: Math.max(0, processingTime),
            timestamp: new Date().toISOString(),
            toAddress: this.formData.clientContactEmail,
          };

          if (errorMessage) {
            metricsPayload.errorMessage = errorMessage;
          }

          // Ensure processingTime is always a valid number
          if (metricsPayload.processingTime == null || isNaN(metricsPayload.processingTime)) {
            metricsPayload.processingTime = 0;
          }

          try {
            await this.addMetricsData(metricsPayload);
          } catch (metricsError) {
            console.warn('Failed to save metrics:', metricsError);
          }
        }

      } catch (error) {
        console.error('❌ Error in startTemplate:', error);
        this.$emit('showNotification', {
          type: 'error',
          message: 'An unexpected error occurred. Please try again.'
        });
      } finally {
        this.showLoading = false;
      }
    },

    async createClientIntroductionEmail() {
      const assignment = this.selectedAssignment;
      if (!assignment) {
        throw new Error('No assignment selected');
      }

      // Prepare user info
      const userEmail = this.getUserDetails?.emailAddress || Office?.context?.mailbox?.userProfile?.emailAddress;
      const userName = this.getUserDetails?.fullName || Office?.context?.mailbox?.userProfile?.displayName || 'User';
      const isJO = this.isLoggedInUserJO;
      console.log('Determining if logged in user is JO...', userEmail, isJO);

      const userInfo = {
        name: userName,
        email: userEmail
      };

      // Get property address and name
      const propertyAddress = getPropertyAddressOrName(assignment, false) || 'the property';
      const propertyName = getPropertyAddressOrName(assignment, true) || '';

      // Get primary valuer name
      const primaryValuer = assignment.primaryValuerName ?? 'the primary valuer';

      // Build CC list; if the JO is sending, ensure the Primary Valuer is included.
      // If a non-JO is sending, ensure the Job Originator is included.
      const ccEmails = [...(this.selectedCCEmails || [])];
      if (this.isLoggedInUserJO && assignment.primaryValuerEmail) {
        const pve = assignment.primaryValuerEmail.toLowerCase().trim();
        if (!ccEmails.some(e => e.toLowerCase().trim() === pve)) {
          ccEmails.push(assignment.primaryValuerEmail);
        }
      } else if (!this.isLoggedInUserJO && assignment.originatorEmail) {
        const oe = assignment.originatorEmail.toLowerCase().trim();
        if (!ccEmails.some(e => e.toLowerCase().trim() === oe)) {
          ccEmails.push(assignment.originatorEmail);
        }
      }
      
      const fileId = assignment.code || '';
      const clientReference = this.assignmentOverviewData?.valuationAssignmentInformation?.clientReferenceNumber || '';

      let emailContent;
      if(isJO && userEmail == assignment.primaryValuerEmail) {
        // JO and Primary Valuer template - if logged in user is JO as well as primary valuer
        const selectedParagraph = this.formData.selectedIntroParagraphObject;
        const useDefaultIntro = !selectedParagraph || selectedParagraph.id === 'default';
        const introContent = selectedParagraph?.content || '';
        const isNewEmail = !this.formData.replyToSelectedEmail;
        const dynamicData = {
          clientContactFirstName: this.formData.clientContactFirstName,
          propertyAddress: propertyAddress,
          propertyName: propertyName,
          primaryValuerName: primaryValuer,
          fileId: fileId,
          clientReference: clientReference,
          useDefaultIntro: useDefaultIntro,
          introContent: introContent,
          isNewEmail: isNewEmail
        };
        const accessToken = await getGraphToken();
        emailContent = await generateEmailTemplate(
          'Client Introduction PV and JO',
          userInfo,
          dynamicData,
          accessToken
        );
      }
      else if (isJO) {
        // JO template — introduce the Primary Valuer to the client
        const selectedParagraph = this.formData.selectedIntroParagraphObject;
        const useDefaultIntro = !selectedParagraph || selectedParagraph.id === 'default';
        const introContent = selectedParagraph?.content || '';
        const isNewEmail = !this.formData.replyToSelectedEmail;

        const dynamicData = {
          clientContactFirstName: this.formData.clientContactFirstName,
          propertyAddress: propertyAddress,
          propertyName: propertyName,
          primaryValuerName: primaryValuer,
          fileId: fileId,
          clientReference: clientReference,
          useDefaultIntro: useDefaultIntro,
          introContent: introContent,
          isNewEmail: isNewEmail
        };

        const accessToken = await getGraphToken();
        emailContent = await generateEmailTemplate(
          'Client Introduction',
          userInfo,
          dynamicData,
          accessToken
        );
      } else {
        // Non-JO (e.g. Primary Valuer) template — introduce themselves, reference the originator
        const originatorFullName =
          assignment.originator ||
          'the Job Originator';

        const selectedParagraph = this.formData.selectedIntroParagraphObject;
        const useDefaultIntro = !selectedParagraph || selectedParagraph.id === 'default';
        const introContent = selectedParagraph?.content || '';
        const isNewEmail = !this.formData.replyToSelectedEmail;

        const dynamicData = {
          clientContactFirstName: this.formData.clientContactFirstName,
          originatorFullName: originatorFullName,
          propertyAddress: propertyAddress,
          propertyName: propertyName,
          fileId: fileId,
          clientReference: clientReference,
          useDefaultIntro: useDefaultIntro,
          introContent: introContent,
          isNewEmail: isNewEmail
        };

        const accessToken = await getGraphToken();
        emailContent = await generateEmailTemplate(
          'Client Introduction PV',
          userInfo,
          dynamicData,
          accessToken
        );
      }

      // Store email subject for audit and metrics
      let emailSubject = emailContent.subject;

      // Check if user wants to reply to selected email
      if (
        this.formData.replyToSelectedEmail &&
        this.emailData &&
        this.emailData.subject &&
        window.Office?.context?.mailbox?.item
      ) {
        // Reply to the selected email
        emailSubject = this.createReplySubject(this.emailData.subject);
        this.createReplyToSelectedEmail(emailContent);
      } else if (window.Office?.context?.mailbox?.displayNewMessageForm) {
        // Create new email with template and signature
        await this.createEmailWithTemplateAndSignature(emailContent, ccEmails);
      } else {
        // Fallback to mailto
        const plainTextBody = emailContent.htmlBody.replace(/<[^>]*>/g, '');
        const ccParam = ccEmails.length > 0 ? `&cc=${ccEmails.join(',')}` : '';
        const mailtoUrl = `mailto:${this.formData.clientContactEmail}?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(plainTextBody)}${ccParam}`;
        
        window.location.href = mailtoUrl;

        this.$emit('showNotification', {
          type: 'info',
          message: 'Email client opened with Client Introduction content.'
        });
      }

      // Mark email as complete with visual indicator
      markEmailAsCompleteNew(
        'completeNotification',
        'Client Introduction email created successfully.',
        undefined,
        true
      );

      // Save audit record
      await this.saveRecentAction(emailSubject);

      // Close the dialog
      this.$emit('close');

      // Return email subject for metrics tracking
      return emailSubject;
    },

    async createEmailWithTemplateAndSignature(emailContent, ccEmails = []) {
      try {
        // Prepare email body with signature for new compose emails
        let finalHtmlBody = emailContent.htmlBody;
        
        // Add signature to new emails (not replies)
        // Check for meaningful signature content (not just empty HTML tags)
        const signatureHtml = this.formData.signature || '';
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = signatureHtml;
        const signatureText = tempDiv.textContent || tempDiv.innerText || '';
        
        if (signatureHtml.trim() && signatureText.trim()) {
          // Save signature to roaming settings when it's being used
          try {
            await this.saveSignatureForFutureUse(signatureHtml);
          } catch (saveError) {
            console.warn('Could not save signature:', saveError.message);
          }
          
          // Use the method that preserves rich text formatting
          const cleanedSignature = this.preserveRichTextFormatting(signatureHtml);
          
          // Add signature with preserved formatting and line breaks
          finalHtmlBody += '<br>' + cleanedSignature;
        }

        // Build recipients
        const ccRecipients = ccEmails.map(email => ({
          emailAddress: email
        }));

        const emailData = {
          toRecipients: [this.formData.clientContactEmail],
          subject: emailContent.subject,
          htmlBody: finalHtmlBody
        };

        if (ccRecipients.length > 0) {
          emailData.ccRecipients = ccRecipients;
        }

        Office.context.mailbox.displayNewMessageForm(emailData);

        this.$emit('showNotification', {
          type: 'success',
          message: 'Client Introduction email created successfully!'
        });

        return true;
      } catch (error) {
        console.error('❌ Error creating email with signature:', error);
        this.$emit('showNotification', {
          type: 'error',
          message: 'Failed to create email. Please try again.'
        });
        return false;
      }
    },

    createReplySubject(originalSubject) {
      if (!originalSubject || !originalSubject.trim()) {
        return 'RE: ';
      }
      // Remove any existing RE: or FW: prefixes to get the clean subject
      let cleanSubject = originalSubject.trim();
      // Remove RE:, FW:, Fwd:, etc. prefixes (case insensitive)
      cleanSubject = cleanSubject.replace(/^(RE:|FW:|FWD:|Fwd:)\s*/i, '').trim();
      // Also handle multiple nested prefixes like "RE: FW: RE: original subject"
      while (/^(RE:|FW:|FWD:|Fwd:)\s*/i.test(cleanSubject)) {
        cleanSubject = cleanSubject.replace(/^(RE:|FW:|FWD:|Fwd:)\s*/i, '').trim();
      }
      // Return with proper RE: prefix
      return `RE: ${cleanSubject}`;
    },

    async saveRecentAction(subjectLine) {
      try {
        const propertyName = this.selectedAssignment?.propertyName || '';
        const clientContactName = `${this.formData.clientContactFirstName} ${this.formData.clientContactLastName}`.trim();

        const payload = {
          action: ACTION_TYPES.CLIENT_INTRODUCTION,
          entityIds: [this.selectedAssignment.viewEventId],
          fileId: this.selectedAssignment.code,
          propertyContact: clientContactName,
          subjectLine: subjectLine,
          toAddress: this.formData.clientContactEmail,
        };
        await handleRecentActions(payload, ACTION_TYPES.CLIENT_INTRODUCTION);
      } catch (error) {
        console.error('Error saving recent action:', error);
        // Don't throw - audit failure shouldn't block user workflow
      }
    },

    createReplyToSelectedEmail(emailContent) {
      try {
        const item = Office.context.mailbox.item;
        const clientContactEmail = this.formData.clientContactEmail;

        // Check if client contact email is in the original recipients
        const originalToEmails = item.to?.map((recipient) => recipient.emailAddress) || [];
        const originalCcEmails = item.cc?.map((recipient) => recipient.emailAddress) || [];

        const isClientContactInTo = originalToEmails.includes(clientContactEmail);
        const isClientContactInCc = originalCcEmails.includes(clientContactEmail);
        const isClientContactAnywhere = isClientContactInTo || isClientContactInCc;

        // Check if additional recipients are in CC
        const missingAdditionalRecipients = this.selectedCCEmails.filter(
          email => !originalCcEmails.includes(email) && !originalToEmails.includes(email)
        );

        // Create reply with template content
        item.displayReplyAllForm({
          htmlBody: emailContent.htmlBody,
        });

        // Build notification message based on missing recipients
        const messages = [];
        if (!isClientContactAnywhere && clientContactEmail) {
          messages.push('Client Contact');
        }
        if (missingAdditionalRecipients.length > 0) {
          messages.push('additional recipients');
        }

        if (messages.length > 0) {
          this.$emit('showNotification', {
            type: 'info',
            message: `Reply Created. Please add ${messages.join(' and ')} to recipients.`,
            timeout: 30000,
          });
        } else if (isClientContactInCc && !isClientContactInTo && clientContactEmail) {
          this.$emit('showNotification', {
            type: 'success',
            message: 'Reply created with Client Introduction content.',
          });
        } else {
          this.$emit('showNotification', {
            type: 'success',
            message: 'Reply created with Client Introduction content.',
          });
        }
      } catch (error) {
        console.error('❌ Error creating reply:', error);
        this.$emit('showNotification', {
          type: 'error',
          message: 'Failed to create reply. Please try again.'
        });
      }
    }
  }
};
</script>

<style scoped>
.v-col-6 {
    flex: 1 0 45% !important;
}

.client-introduction-wrapper input:-webkit-autofill,
.client-introduction-wrapper input:-webkit-autofill:hover,
.client-introduction-wrapper input:-webkit-autofill:focus,
.client-introduction-wrapper input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
  -webkit-text-fill-color: #000 !important;
  transition: background-color 5000s ease-in-out 0s;
}

.client-introduction-wrapper input[autocomplete="new-password"]::-webkit-contacts-auto-fill-button,
.client-introduction-wrapper input[autocomplete="new-password"]::-webkit-credentials-auto-fill-button {
  display: none !important;
}

.client-introduction-wrapper {
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
  padding: 0;
  position: relative;
}

.form-disabled {
  pointer-events: none;
  opacity: 0.6;
  user-select: none;
}

.client-introduction-wrapper * {
  box-sizing: border-box;
}

.client-introduction-wrapper .form-field {
  display: block !important;
  width: 100% !important;
}

.form-field, .field-group {
  margin-bottom: 16px;
  width: 100%;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 16px;
  color: #000;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.loading-text {
  font-size: 14px;
  color: #666;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

.field-error :deep(.v-field) {
  border-color: #f44336 !important;
}

.field-error :deep(.v-field--focused) {
  border-color: #f44336 !important;
}

.error-message {
  color: #f44336;
  font-size: 14px;
  margin-top: 4px;
  font-weight: 400;
}

.client-introduction-wrapper :deep(.v-text-field) {
  font-size: 14px;
}

.client-introduction-wrapper :deep(.v-text-field .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  padding: 0 16px;
}

.client-introduction-wrapper :deep(.v-text-field .v-field__input::placeholder) {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
  height: 48px;
  text-align: left;
}

.client-introduction-wrapper :deep(.v-field) {
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
}

.client-introduction-wrapper :deep(.v-field--focused) {
  border: 1px solid #5A5A5A !important;
  box-shadow: none !important;
}

.client-introduction-wrapper :deep(.v-field--error) {
  border: 1px solid #f44336 !important;
}

.client-introduction-wrapper :deep(.v-field__outline) {
  display: none !important;
}

.client-introduction-wrapper .form-field .v-row {
  margin: 0 !important;
  gap: 8px !important;
}

.client-introduction-wrapper .form-field .v-col {
  padding: 0 !important;
}

.reply-checkbox-section {
  margin-bottom: 16px;
  padding: 0;
}

.reply-checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  margin-left: -8px;
}

.reply-checkbox {
  margin-left: 0;
  margin-right: 0;
}

.checkbox-main-label {
  margin-bottom: 0 !important;
  flex: 1;
}

.checkbox-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  padding: 0;
  margin-left: 0;
}

.reply-checkbox :deep(.v-input__control) {
  min-height: auto;
}

.reply-checkbox :deep(.v-input__details) {
  display: none;
}

.reply-checkbox :deep(.v-selection-control) {
  align-items: flex-start;
  min-height: auto;
}

.reply-checkbox :deep(.v-selection-control__wrapper) {
  height: 20px;
}

.signature-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signature-vue-editor {
  font-family: Arial, sans-serif !important;
  min-height: 150px;
  width: 100%;
  border: 1px solid #ddd !important;
  border-radius: 4px;
}

.signature-vue-editor :deep(.ql-editor) {
  font-family: Arial, sans-serif !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  min-height: 120px;
  padding: 12px !important;
}

.signature-vue-editor :deep(.ql-toolbar) {
  border-bottom: 1px solid #ddd;
  background: #f9f9f9;
  border-radius: 4px 4px 0 0;
}

.signature-vue-editor :deep(.ql-container) {
  border-radius: 0 0 4px 4px;
  font-family: Arial, sans-serif !important;
}

.signature-vue-editor :deep(.ql-editor.ql-blank::before) {
  content: 'Enter your signature here...';
  color: #aaa;
  font-style: normal;
}

.signature-char-count {
  text-align: right;
  font-size: 11px;
  color: #666;
  margin-top: 4px;
  padding-right: 8px;
}

.signature-char-count.error {
  color: #f44336;
}

.forward-primary-banner {
  background: #003F2D14;
  border-radius: 4px;
  padding: 4px 3px 4px 4px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
}

.forward-primary-label {
  color: #000000;
  font-family: Calibre-R, Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  margin-right: 8px;
  white-space: nowrap;
}

.forward-primary-name {
  color: #003F2D;
  font-family: Calibre-R, Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  word-break: break-word;
  margin-left: auto;
  text-align: right;
}
</style>
